import { createStitches } from '@stitches/react';
import type * as Stitches from '@stitches/react';

import palette from '@styles/palette';
import global from '@styles/global';
import { generateUnits, UNITS } from '@styles/units';

const sizingUnits = generateUnits();

export const { styled, css, theme, createTheme, getCssText, globalCss, keyframes, config, reset } = createStitches({
  theme: {
    colors: {
      ...palette,

      // *******************************
      // MAIN COLORS
      // *******************************

      // primary colors
      darkBlue: '$darkBlue300',
      pink: '$pink300',
      green: '$green300',
      yellow: '$yellow300',

      // secondary colors
      warmGrey: '$warmGrey300',
      coolGrey: '$coolGrey300',
      darkGrey: '$darkGrey300',
      seafoam: '$seafoam300',
      white: '#ffffff',

      // tertiary colors
      red: '$red300',
      blue: '$blue300',
      charcoal: '$charcoal300',
      teal: '$teal300',

      black: '#000',

      primary: '$darkBlue',
      secondary: '$white',
      tertiary: '$blue',

      // *******************************
      // UI COLORS
      // *******************************

      // text links
      textLink: '$blue300',
      textLinkDark: '$blue200',
      textLinkDisabled: '$darkGray100',

      // notifications & messages
      success: '$green300',
      successLight: '$green700',
      error: '$red300',
      errorLight: '$red800',
      info: '$blue300',
      infoLight: '$blue800',

      // text
      textPrimary: '$charcoal300',
      textSecondary: '$darkGray100',
      textAccent: '$white',

      // icons
      iconography: '$darkGray100',

      // selected item
      selectedRow: '$blue800',
      selectedRowOutline: '$blue700',

      // focus outline color
      focusOutline: '-webkit-focus-ring-color',
    },
    space: {
      ...sizingUnits,
      layoutPadding: '12px',
      iconPadding: '14px',
    },
    lineHeights: sizingUnits,
    sizes: {
      ...sizingUnits,
      layoutMaxWidth: '932px',
      notificationMinWidth: '200px',
      image: '88px',
      dropdownListWidth: '200px',
    },
    fontSizes: {
      normal: '14px',
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '20px',
      xl: '24px',
      xxl: '26px',
      '2xl': '28px',
      '3xl': '32px',
      '3xxl': '34px',
      '4xl': '36px',
      '5xl': '48px',
    },
    radii: {
      ...generateUnits([1, 2, 3, 4, 5, 6, 8, 20], 1, UNITS.PX),
      round: '50%',
      pill: '9999px',
      togglePillLeft: '$pill 0 0 $pill',
      togglePillRight: '0 $pill $pill 0',
    },
    zIndices: {
      devTool: '900',
      dropdown: '500',
      loader: '100',
      modal: '600',
      notification: '800',
      sidebar: '400',
      stickyHeader: '300',
      map: '200',
      mapLegend: '400',
      tooltip: '700',
    },
    borderWidths: {
      ...generateUnits([0, 1, 2, 3, 4, 5], 1, UNITS.PX),
    },
    shadows: {
      banner: '$sizes$1 $sizes$4 $sizes$12 rgba(0, 0, 0, 0.15)',
      card: '$sizes$1 $sizes$4 $sizes$12 rgba(0, 0, 0, 0.15)',
      chart: '$sizes$0 $sizes$0 $sizes$2 rgba(0, 0, 0, 0.15)',
      footer: '$sizes$4 -$sizes$4 $sizes$10 rgba(0, 0, 0, 0.03)',
      menu: '$sizes$1 $sizes$4 $sizes$12 rgba(0, 0, 0, 0.15)',
      sidebar: '$sizes$1 $sizes$0 $sizes$5 rgba(0, 0, 0, 0.1)',
      stickyNav: '$sizes$0 $sizes$4 $sizes$10 rgba(0, 0, 0, 0.1)',
      tabGroup: 'inset 0 -$sizes$1 $sizes$2 -$sizes$1 rgba(0, 0, 0, 0.15)',
      tooltip: '$sizes$1 $sizes$1 $sizes$2 rgba(0, 0, 0, 0.15)',
    },
    fontWeights: {
      regular: 344,
      medium: 500,
      xmedium: 600,
      bold: 620,
      bolder: 700,
      xbold: 740,
    },
    letterSpacings: {
      ...generateUnits([0, 0.5, 1], 1),
    },
    borderStyles: {},
    transitions: {},
    fonts: {},
    studyInsights: {
      containerMaxWidth: '1390px',
      headerMaxWidth: '1330px',
      fontBold: '740',
      fontXs: '24px',
      fontMd: '34px',
      fontXl: '48px',
      fontXxl: '56px',
      sidebarWidthClose: '92px',
      sidebarWidthOpen: '270px',
      sideSeparationLeft: '144px',
      shadowBox: '2px 6px 20px 4px rgba(24, 39, 66, 0.06)',
    },
    stickyNav: {
      height: '57px',
    },
  },
  media: {
    xs: '(max-width: 735px)',
    sm: '(min-width: 736px)',
    md: '(min-width: 1024px)',
    lg: '(min-width: 1440px)',
  },
  utils: {
    p: (value: Stitches.PropertyValue<'padding'>) => ({
      padding: value,
    }),
    pt: (value: Stitches.PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
    }),
    pr: (value: Stitches.PropertyValue<'paddingRight'>) => ({
      paddingRight: value,
    }),
    pb: (value: Stitches.PropertyValue<'paddingBottom'>) => ({
      paddingBottom: value,
    }),
    pl: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
    }),
    px: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: Stitches.PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
      paddingBottom: value,
    }),

    m: (value: Stitches.PropertyValue<'margin'>) => ({
      margin: value,
    }),
    mt: (value: Stitches.PropertyValue<'marginTop'>) => ({
      marginTop: value,
    }),
    mr: (value: Stitches.PropertyValue<'marginRight'>) => ({
      marginRight: value,
    }),
    mb: (value: Stitches.PropertyValue<'marginBottom'>) => ({
      marginBottom: value,
    }),
    ml: (value: Stitches.PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
    }),
    mx: (value: Stitches.PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: Stitches.PropertyValue<'marginTop'>) => ({
      marginTop: value,
      marginBottom: value,
    }),

    size: (value: Stitches.PropertyValue<'width'>) => ({
      width: value,
      height: value,
    }),
  },
});

export const globalStyles = globalCss(global);
